import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCaisses, useComptesBancaires } from '@/hooks/queries/useBanques';
import {
  useBulkEncaisserFactures,
  type FactureRecouvrementRow,
} from '@/hooks/queries/useRecouvrementEntreprises';
import type { DossierPaiementMode } from '@/hooks/queries/usePaiementsFormateurs';
import { MoneyAmount } from '@/components/design-system';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  factures: FactureRecouvrementRow[];
  entrepriseId: string;
  onDone?: () => void;
}

const MODES: { code: DossierPaiementMode; label: string }[] = [
  { code: 'virement', label: 'Virement' },
  { code: 'cheque', label: 'Chèque' },
  { code: 'especes', label: 'Espèces' },
  { code: 'ccp', label: 'CCP' },
  { code: 'carte', label: 'Carte' },
];

export function MarquerFacturesEncaisseesEnLotDialog({
  open,
  onOpenChange,
  factures,
  entrepriseId,
  onDone,
}: Props): JSX.Element {
  const bulk = useBulkEncaisserFactures();
  const { data: caisses = [] } = useCaisses(entrepriseId);
  const { data: comptes = [] } = useComptesBancaires(entrepriseId);

  const [dateReglement, setDateReglement] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState<DossierPaiementMode>('virement');
  const [caisseId, setCaisseId] = useState('');
  const [compteId, setCompteId] = useState('');

  useEffect(() => {
    if (!open) return;
    setDateReglement(new Date().toISOString().slice(0, 10));
    setMode('virement');
    setCaisseId('');
    setCompteId('');
  }, [open]);

  useEffect(() => {
    if (mode === 'especes' && caisses.length > 0 && !caisseId) setCaisseId(caisses[0]?.id ?? '');
    if (mode !== 'especes' && comptes.length > 0 && !compteId) setCompteId(comptes[0]?.id ?? '');
  }, [mode, caisses, comptes, caisseId, compteId]);

  const isEspeces = mode === 'especes';
  const targetMissing = isEspeces ? !caisseId : !compteId;
  const eligibleFactures = factures.filter((f) => f.statut !== 'regle');
  const totalReste = eligibleFactures.reduce((a, f) => a + (f.montant_ttc - f.montant_encaisse), 0);

  const handleConfirm = async (): Promise<void> => {
    if (eligibleFactures.length === 0) {
      toast.warning('Aucune facture à solder (toutes déjà réglées)');
      return;
    }
    if (targetMissing) {
      toast.error(isEspeces ? 'Sélectionnez la caisse' : 'Sélectionnez le compte bancaire');
      return;
    }
    try {
      const result = await bulk.mutateAsync({
        factures: eligibleFactures,
        dateReglement,
        modePaiement: mode,
        caisseId: isEspeces ? caisseId : null,
        compteBancaireId: isEspeces ? null : compteId,
      });
      if (result.errors.length === 0) {
        toast.success(`${result.success} facture(s) soldée(s) · opérations Caisse créées`);
      } else {
        toast.warning(
          `${result.success} OK · ${result.errors.length} échec(s) — ex: ${result.errors[0]?.message ?? ''}`,
        );
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Encaisser en lot · {eligibleFactures.length} facture(s)</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-widest text-emerald-700">
                Total à encaisser
              </span>
              <MoneyAmount amount={totalReste} variant="large" />
            </div>
            {factures.length - eligibleFactures.length > 0 && (
              <div className="mt-1 text-xs text-slate-600">
                {factures.length - eligibleFactures.length} facture(s) déjà réglée(s) seront
                ignorées
              </div>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 text-xs">
            Chaque facture sera soldée intégralement (reste à encaisser = TTC − déjà encaissé).
            <strong> Une opération Caisse ENTRÉE par facture</strong>, mêmes date / mode / cible.
          </div>

          <Field label="Date de règlement">
            <input
              type="date"
              value={dateReglement}
              onChange={(e) => setDateReglement(e.target.value)}
              className="input"
              required
            />
          </Field>

          <Field label="Mode">
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {MODES.map((m) => (
                <button
                  key={m.code}
                  type="button"
                  onClick={() => setMode(m.code)}
                  className={cn(
                    'rounded-lg border-2 px-2 py-2 text-xs font-semibold transition-all',
                    mode === m.code
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </Field>

          {isEspeces ? (
            <Field
              label="Caisse créditée"
              hint={caisses.length === 0 ? '⚠ Aucune caisse disponible' : undefined}
              warn={caisses.length === 0}
            >
              <select
                value={caisseId}
                onChange={(e) => setCaisseId(e.target.value)}
                className="input"
                required
              >
                <option value="">— Sélectionner —</option>
                {caisses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                    {c.site ? ` · ${c.site}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field
              label="Compte bancaire crédité"
              hint={comptes.length === 0 ? '⚠ Aucun compte disponible' : undefined}
              warn={comptes.length === 0}
            >
              <select
                value={compteId}
                onChange={(e) => setCompteId(e.target.value)}
                className="input"
                required
              >
                <option value="">— Sélectionner —</option>
                {comptes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.intitule} {c.numero_compte ? `· ${c.numero_compte}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={bulk.isPending || targetMissing || eligibleFactures.length === 0}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {bulk.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirmer · {eligibleFactures.length} facture(s)
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  warn,
  children,
}: {
  label: string;
  hint?: string;
  warn?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">{label}</span>
      {children}
      {hint && (
        <span
          className={cn(
            'mt-0.5 block',
            warn ? 'text-xs font-medium text-amber-600' : 'text-[10px] text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
